// aluSeriesResolver.js — derives the exact aluminum series from what the
// vendor already captures (product_type candidates + mechanism + panel_config
// + an optional economy toggle), so the vendor thinks in PRODUCTS, not raw
// profile-series codes (152 vs 159 vs 234 vs 308 is an engineering/commercial
// outcome, not something the vendor should type).
//
// Data source: catalogAPI.aluTypeSeries() -> { product_type: [candidate...] }
// where each candidate carries commercial_class, track_count, display_name,
// thickness_class, factor (migration 056 + enriched endpoint).
//
// TWO INDEPENDENT AXES — do not conflate:
//   * Engineering (track_count): the lift-slide multi-rail family is built on
//     the 159S base (159mm = 2 rails; +tracks -> 234S=3, 308S=4). "base" here
//     means rail geometry, NOT the default series.
//   * Commercial tier (at 2 rails): 159S = PREMIUM (default), 152S = ECONOMY
//     (only when the client explicitly asks for the cheaper profile).
//   These coexist: the default entry at 2 rails is the PREMIUM 159S, even
//   though 234S/308S are geometrically "based on" 159S. Different meanings of
//   the word "base".
//
// Doctrine: aluminum is a PRE-QUOTE estimate; factory confirms per project.
// On a tie across the axes we can see (e.g. 152S vs 159S, both Lift-Slide /
// 2-rail) we default to the PREMIUM and flag rough=true, rather than block the
// vendor or pick silently. Doorwin can still force a profile by opening width
// (a spec we don't hold yet -> factory-gated, same as OXXO 3-vs-4 rail).
//
// The resolved tier needs NO extra persistence — it collapses into the concrete
// model_id the line already saves. The economy flag is transient UI input only.
//
// Pure functions, no React, no side effects. Frontend-only.

// Count operable/total panels from a panel_config string.
// Sliding notation "OXXO" -> 4 (letters). Bi-fold "4P 3L-1R" -> 4 (leading NxP).
// Falls back to null when there's nothing to read.
export function panelCountFromConfig(panel_config) {
  const s = String(panel_config || '').trim();
  if (!s) return null;
  const mP = s.match(/^(\d+)\s*P/i);        // "4P ..."
  if (mP) return parseInt(mP[1], 10);
  const mOX = s.match(/^[OXox]+/);          // "OXXO"
  if (mOX) return mOX[0].length;
  return null;
}

// Count OPERABLE leaves (the X's). Rails are consumed by moving leaves only:
// the O's are fixed and ride no track. "OXXO" = 4 panels but 2 operables ->
// 2 rails, which is what Doorwin actually quotes (corpus: OXXO -> 159S 33x,
// 152S 11x, plus 3 specs that literally say "2 track"). Counting letters
// instead of X's sent every OXXO to 3-rail 234S - wrong on the most common
// lift-slide config in the catalog, and 18% over on price.
// Bifold "4P 3L-1R": every leaf folds, so operables = total.
// Do NOT reuse panelCountFromConfig here - that one feeds Buiria install,
// which charges per PANEL (all of them), not per operable.
export function operableCountFromConfig(panel_config) {
  const s = String(panel_config || '').trim();
  if (!s) return null;
  const mP = s.match(/^(\d+)\s*P/i);        // "4P 3L-1R" -> 4
  if (mP) return parseInt(mP[1], 10);
  const mOX = s.match(/^[OXox]+/);          // "OXXO" -> 2
  if (mOX) {
    const x = (mOX[0].match(/[Xx]/g) || []).length;
    return x > 0 ? x : null;
  }
  return null;
}

// Distinct non-null commercial classes among candidates, order-preserving.
function distinctClasses(candidates) {
  const seen = [];
  for (const c of candidates) {
    const cc = c.commercial_class;
    if (cc && !seen.includes(cc)) seen.push(cc);
  }
  return seen;
}

// Desempate entre candidatos.
//
// BUG QUE ESTO CORRIGE (visto en MQ-2026-0010): esta funcion ordenaba por
// `factor` y tomaba el MAS CARO cuando no habia economy. La regla se diseno
// para el par 152S/159S, donde "premium por default" es correcto porque son
// dos gamas del mismo producto. Pero para Tilt&Turn hay 7 candidatos y
// NINGUNO tiene track_count, asi que pickByTracks caia directo aqui y elegia
// 85S (factor 1.87) sobre 65S/70S/80S/95S/E80/103. Una tilt-turn normal de
// 50x74 salia en $3,723 en vez de ~$1,991. Casi el doble, en cada cotizacion
// de ventana. Aplicaba igual a Fixed/Picture, Casement, Awning y Hinged Door.
//
// REGLA NUEVA:
//   1. Si hay commercial_tier declarado (migr 084), ese decide: es atributo
//      de producto, no salida de calibracion. El factor cambia con cada
//      recalibracion; el tier no.
//   2. Si NO hay tier, el precio no puede desempatar. Se toma 80S, la serie
//      base del catalogo y la mas vendida (1,612 lineas del corpus Doorwin).
//      El vendedor puede cambiarla; lo que no puede es que el sistema le
//      cobre el doble en silencio.
//   3. economy=true sigue pidiendo el mas barato: eso si es eleccion
//      explicita del cliente.
const SERIE_BASE = '80S';

function pickTier(tied, economy) {
  const pool = tied.filter(c => c.factor != null).length
    ? tied.filter(c => c.factor != null)
    : tied;
  if (pool.length <= 1) return pool[0] || null;

  const porFactor = [...pool].sort((a, b) => Number(a.factor) - Number(b.factor));

  // El cliente pidio el perfil barato: eso es decision, no adivinanza.
  if (economy) return porFactor[0];

  // Par de gama declarado (152S Economy / 159S Premium): gana el Premium.
  const premium = pool.find(c => c.commercial_tier === 'Premium');
  if (premium && pool.some(c => c.commercial_tier === 'Economy')) return premium;

  // Sin tier: el factor NO desempata. Base del catalogo si esta disponible.
  const base = pool.find(c => c.code === SERIE_BASE);
  if (base) return base;

  // Ni tier ni base entre los candidatos -> el mas barato, que es el error
  // mas barato de cometer: subcotizar se detecta al confirmar con fabrica,
  // sobrecotizar se detecta cuando el cliente ya se fue.
  return porFactor[0];
}

// Map a panel count to the matching track_count, then break any tier tie.
// 2 panels -> 2-rail; 3 -> 3-rail; 4+ -> default 3-rail (rough; factory
// confirms 4-rail). When several series share the target track_count
// (152S vs 159S), pickTier decides: premium default, economy on request.
// Returns { candidate, rough }.
// Rieles reales a partir de la cadena de paneles. Regla derivada del corpus
// (22 titulos donde Doorwin escribio el conteo, ver alu_corpus_lineas):
//   rieles = hojas apiladas del lado mas cargado + 1 si hay paneles fijos
// Verificado contra: XO=2, OXXO=2, XXO=3, XXX=3, XXXO=4, OXXX=4, OXXXXO=3.
// Sin fijos que anclen la direccion la notacion es ambigua (XXXX puede ser
// 2+2 al centro o 4 a un lado). Decision A: par -> se reparte, impar -> un
// solo lado; se marca rough para que fabrica confirme.
// Devuelve { tracks, rough } o null si no se puede leer la cadena.
export function tracksFromConfig(panel_config) {
  const s = String(panel_config || '').trim().toUpperCase();
  if (!s || !/^[OX]+$/.test(s) || !/X/.test(s)) return null;
  const oIzq = /^O/.test(s);
  const oDer = /O$/.test(s);
  const hayFijos = /O/.test(s);
  const moviles = (s.match(/X/g) || []).length;
  let apiladas, rough = false;
  if (oIzq && oDer) {
    apiladas = Math.ceil(moviles / 2);          // fijos en ambos extremos: se reparten
  } else if (oIzq || oDer) {
    apiladas = moviles;                          // fijo de un lado: todas al otro
  } else if (hayFijos) {
    apiladas = Math.ceil(moviles / 2);          // fijos al centro (XOOX): abren hacia afuera
  } else {
    apiladas = moviles % 2 === 0 ? moviles / 2 : moviles;
    rough = true;                                // sin fijos: direccion asumida
  }
  let tracks = apiladas + (hayFijos ? 1 : 0);
  // Piso: dos hojas moviles no comparten riel (se chocarian).
  if (moviles > 1 && tracks < 2) { tracks = 2; }
  // Techo: 308S (4 rieles) es el perfil mas ancho que existe. Arriba de eso
  // fabrica reparte las hojas; el corpus factura OXXXXX como 159S (SO11864).
  if (tracks > 4) { tracks = 4; rough = true; }
  return { tracks, rough };
}
function pickByTracks(candidates, operableCount, economy, panel_config) {
  const withTracks = candidates
    .filter(c => c.track_count != null)
    .sort((a, b) => a.track_count - b.track_count);
  if (withTracks.length === 0) {
    return { candidate: pickTier(candidates, economy) || candidates[0] || null, rough: true };
  }

  let target;
  let targetRough = false;
  if (operableCount == null) {
    target = withTracks[0].track_count;   // lowest as placeholder
    targetRough = true;
  } else {
    // La cadena manda: OXXO y XXO tienen 2 operables pero rieles distintos.
    const t = tracksFromConfig(panel_config);
    if (t) {
      target = t.tracks;
      targetRough = t.rough;
    } else {
      target = operableCount <= 2 ? 2 : 3;
      targetRough = true;
    }
  }

  let matches = withTracks.filter(c => c.track_count === target);
  if (matches.length === 0) {
    // No exact track match -> nearest available, flagged rough.
    matches = withTracks;
    targetRough = true;
  }

  const candidate = pickTier(matches, economy);

  // Tier ambiguity: several series tied on track_count and vendor did NOT
  // ask for economy -> the premium default is a guess, so rough. An explicit
  // economy pick is a choice, not a guess (Doorwin-by-width override is a
  // universal factory step, not modeled by rough).
  const tierAmbiguous = matches.length > 1 && !economy;

  return { candidate, rough: targetRough || tierAmbiguous };
}

// Main resolver.
//   candidates  : array from typeSeries[product_type] (may be undefined/empty)
//   mechanism   : the chosen commercial_class (e.g. 'Lift-Slide'), or ''
//   panel_config: current panel_config string
//   economy     : true when the client explicitly requested the cheaper profile
// Returns:
//   { needsMechanism, mechanismOptions, model, rough, unresolved }
export function resolveSeries(candidates, { mechanism = '', panel_config = '', economy = false } = {}) {
  const list = Array.isArray(candidates) ? candidates : [];
  if (list.length === 0) {
    return { needsMechanism: false, mechanismOptions: [], model: null, rough: false, unresolved: true };
  }

  const classes = distinctClasses(list);
  const needsMechanism = classes.length > 1;

  // If the type splits by mechanism and none is chosen yet, we can't resolve.
  if (needsMechanism && !mechanism) {
    return { needsMechanism: true, mechanismOptions: classes, model: null, rough: false, unresolved: true };
  }

  // Narrow to the chosen mechanism (if any).
  const scoped = needsMechanism
    ? list.filter(c => c.commercial_class === mechanism)
    : list;

  if (scoped.length === 0) {
    return { needsMechanism, mechanismOptions: classes, model: null, rough: false, unresolved: true };
  }
  if (scoped.length === 1) {
    return { needsMechanism, mechanismOptions: classes, model: scoped[0], rough: false, unresolved: false };
  }

  // Multiple series within the scope -> disambiguate by track_count, then tier.
  const operableCount = operableCountFromConfig(panel_config);
  const { candidate, rough } = pickByTracks(scoped, operableCount, economy, panel_config);
  return { needsMechanism, mechanismOptions: classes, model: candidate, rough, unresolved: candidate == null };
}